import { stringify } from 'query-string';
import { GET_LIST,
  GET_ONE,
  GET_MANY,
  GET_MANY_REFERENCE,
  CREATE,
  UPDATE,
  DELETE, fetchJson } from 'admin-on-rest';
import omitBy from 'lodash/omitBy';

/**
 * Maps admin-on-rest queries to a simple REST API
 *
 * The REST dialect is similar to the one of FakeRest
 * @see https://github.com/marmelab/FakeRest
 * @example
 * GET_LIST     => GET http://my.api.url/posts?sort=['title','ASC']&range=[0, 24]
 * GET_ONE      => GET http://my.api.url/posts/123
 * GET_MANY     => GET http://my.api.url/posts?filter={ids:[123,456,789]}
 * UPDATE       => PUT http://my.api.url/posts/123
 * CREATE       => POST http://my.api.url/posts/123
 * DELETE       => DELETE http://my.api.url/posts/123
 */
export default (apiUrl, httpClient = fetchJson, resources) => {
  /**
   * @param {String} type One of the constants appearing at the top if this file, e.g. 'UPDATE'
   * @param {String} resource Name of the resource to fetch, e.g. 'posts'
   * @param {Object} params The REST request params, depending on the type
   * @returns {Object} { url, options } The HTTP request parameters
   */
  const convertRESTRequestToHTTP = (type, resource, params) => {
    let url = '';
    const options = {};

    switch (type) {
      case GET_LIST: {
        const { page, perPage } = params.pagination;
        const { field, order } = params.sort;
        const query = stringify({
          sort: JSON.stringify([field, order]),
          range: JSON.stringify([
            (page - 1) * perPage,
            (page * perPage) - 1,
          ]),
          filter: JSON.stringify(params.filter),
        });

        url = `${apiUrl}/${resource}?${query}`;
        break;
      }
      case GET_ONE: {
        let id = params.id; // eslint-disable-line
        if (typeof id === 'object' && id._id) {
          id = id._id;
        }
        url = `${apiUrl}/${resource}/${id}`;
        break;
      }
      case GET_MANY: {
        const query = JSON.stringify(params.ids);
        options.method = 'POST';
        options.body = query;
        url = `${apiUrl}/${resource}/search`;
        break;
      }
      case GET_MANY_REFERENCE: {
        const { page, perPage } = params.pagination;
        const { field, order } = params.sort;
        const query = {
          sort: JSON.stringify([field, order]),
          range: JSON.stringify([
            (page - 1) * perPage,
            (page * perPage) - 1,
          ]),
          filter: JSON.stringify({
            ...params.filter,
            [params.target]: params.id,
          }),
        };
        url = `${apiUrl}/${resource}?${stringify(query)}`;
        break;
      }
      case UPDATE: {
        url = `${apiUrl}/${resource}/${params.id}`;
        const data = { ...omitBy(params.data, (value) => typeof value === 'string' && value.length === 0) };
        const user = JSON.parse(localStorage.getItem('user'));
        data.lastUpdatedBy = user.phoneNumber;
        options.method = 'PUT';
        options.body = JSON.stringify(data);
        break;
      }
      case CREATE: {
        url = `${apiUrl}/${resource}`;
        const data = { ...omitBy(params.data, (value) => typeof value === 'string' && value.length === 0) };
        const user = JSON.parse(localStorage.getItem('user'));
        data.submittedBy = user.phoneNumber;
        options.method = 'POST';
        options.body = JSON.stringify(data);
        break;
      }
      case DELETE:
        url = `${apiUrl}/${resource}/${params.id}`;
        options.body = JSON.stringify({ ...params.previousData });
        options.method = 'DELETE';
        break;
      default:
        throw new Error(`Unsupported fetch action type ${type}`);
    }
    return { url, options };
  };

  /**
   * @param {Object} response HTTP response from fetch()
   * @param {String} type One of the constants appearing at the top if this file, e.g. 'UPDATE'
   * @param {String} resource Name of the resource to fetch, e.g. 'posts'
   * @param {Object} params The REST request params, depending on the type
   * @returns {Object} REST response
   */
  const convertHTTPResponseToREST = (response, type, resource, params) => {
    const { headers, json } = response;
    let rs;
    switch (type) {
      case GET_LIST:
      case GET_MANY_REFERENCE: {
        if (!headers.has('content-range')) {
          throw new Error('The Content-Range header is missing in the HTTP Response. The simple REST client expects responses for lists of resources to contain this header with the total number of results to build the pagination. If you are using CORS, did you declare Content-Range in the Access-Control-Expose-Headers header?');
        }

        rs = resources && resources.find((res) => res.name === resource);
        let data;
        if (rs) {
          data = json.map((res) => ({ ...res, id: res[rs.identifier] }));
        } else {
          data = json.map((res) => ({ ...res, id: res._id }));
        }
        return {
          data,
          total: parseInt(
            headers
              .get('content-range')
              .split('/')
              .pop(),
            10
          ),
        };
      }
      case UPDATE:
      case DELETE:
      case GET_ONE: {
        rs = resources && resources.find((res) => res.name === resource);
        const data = { ...json, id: json._id };
        if (rs) {
          data.id = json[rs.identifier];
        }
        return {
          data,
        };
      }
      case CREATE: {
        rs = resources && resources.find((res) => res.name === resource);
        const data = { ...params.data, id: json._id };
        if (rs) {
          data.id = json[rs.identifier];
        }
        return {
          data,
        };
      }
      default:
        return { data: json };
    }
  };

  /**
   * @param {string} type Request type, e.g GET_LIST
   * @param {string} resource Resource name, e.g. "posts"
   * @param {Object} payload Request parameters. Depends on the request type
   * @returns {Promise} the Promise for a REST response
   */
  return (type, resource, params) => {
    const { url, options } = convertRESTRequestToHTTP(
      type,
      resource,
      params
    );
    return httpClient(url, { ...options, credentials: 'include' })
      .then((response) => convertHTTPResponseToREST(response, type, resource, params))
      .catch((e) => {
        if (e.body && e.body.error) {
          throw new Error(e.body.error.length ? e.body.error : 'oh dear this is bad');
        }
        throw e;
      });
  };
};
